// Dashboard admin v2 — rediseño 2026-06-11 con foco en acción.
// Layout: Operación del día (común) + 3 columnas por área (Operativa / Finanzas / Comercial).
// Pensado para 3 personas con focos distintos: Nico (operativa), Roberto (finanzas), Gimena (comercial).
//
// Decisiones:
// - Saco KPIs vanidosos (MRR, Churn, LTV, Ingresos) → viven en /admin/reportes, link abajo
// - Saco "Mi día como repartidor" (vivía en Hero) → /repartidor/hoy
// - Saco componente AnalisisIA → era ruido en home
// - Conservo CTA Agente Marketing arriba (es útil para los 3)
// - Cada card es 100% clickeable con conteo + link directo a la pantalla con filtro

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { adminGetAlertasStock } from '../../lib/portalClienteApi'
import VencimientosFiscalesSection from '../../components/VencimientosFiscalesSection'
import CanalesResumenSection from '../../components/CanalesResumenSection'

export default function AdminDashboard() {
  const { user, profile, isSuperadmin } = useAuth()
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!user?.id) return
    async function load() {
      const hoy = new Date()
      const fechaHoy = hoy.toISOString().slice(0, 10)
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10)
      const dosHorasAtras = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()

      const [
        // ─── OPERACIÓN DEL DÍA (común a los 3) ───
        { count: entregasHoy },
        { count: entregasHoyEntregadas },
        { data: cajaHoy },                    // suma de pagos cobrados hoy
        alertasStock,                         // RPC que ya existe — devuelve {nivel, ...}
        { count: crmNoLeidos },               // conversations con no_leidos > 0

        // ─── OPERATIVA (foco Nico) ───
        { count: crmPendientesPlus2h },       // mensajes sin responder hace +2h
        { count: entregasSinAsignar },        // entregas hoy sin repartidor
        { count: pedidosSinArmar },           // pedidos one-shot pagados sin armar

        // ─── FINANZAS (foco Roberto) ───
        { count: cobrosPendientesHoy },       // pagos pendientes con fecha hoy
        { data: cobrosPendientesMonto },      // suma de monto pendiente
        { count: asientosBorrador },          // asientos sin contabilizar

        // ─── COMERCIAL (foco Gimena) ───
        { count: leadsNuevos },               // leads sin contactar
        { count: consultasAbiertas },         // consultas comerciales abiertas
        { count: postulacionesPorteros },     // postulaciones nuevas

        // ─── CONTEXTO INFORMATIVO ───
        { count: clientesActivos },
        { count: empresasActivas },

        // ─── DESPACHOS ML (nuevo, común) ───
        { data: despachosML },

        // ─── CAJA B2C (pedidos compra única / sueltos pagados hoy) ───
        { data: cajaHoyPedidos },
      ] = await Promise.all([
        supabase.from('entregas').select('*', { count: 'exact', head: true }).eq('fecha_programada', fechaHoy).eq('es_prueba', false),
        supabase.from('entregas').select('*', { count: 'exact', head: true }).eq('fecha_programada', fechaHoy).eq('estado', 'entregada').eq('es_prueba', false),
        // Caja del día: solo pagos reales (excluye es_prueba)
        supabase.from('pagos').select('monto').eq('estado', 'pagado').eq('es_prueba', false).gte('pagado_at', fechaHoy + 'T00:00:00').lte('pagado_at', fechaHoy + 'T23:59:59'),
        safeAlertas(adminGetAlertasStock()),
        // Fix 7-jul-2026 (Nico): auditoría dashboard admin — 4 bugs de data.
        // Bug #1: era 'no_leidos' pero la columna es 'unread_count'. Nico veía siempre 0.
        safeCount(supabase.from('conversations').select('*', { count: 'exact', head: true }).gt('unread_count', 0)),

        safeCount(supabase.from('conversations').select('*', { count: 'exact', head: true }).eq('estado', 'pendiente').eq('last_message_from', 'cliente').lt('last_message_at', dosHorasAtras)),
        supabase.from('entregas').select('*', { count: 'exact', head: true }).eq('fecha_programada', fechaHoy).is('repartidor_id', null).eq('es_prueba', false),
        safeCount(supabase.from('pedidos').select('*', { count: 'exact', head: true }).eq('estado', 'pagado').eq('es_prueba', false).in('tipo', ['compra_unica', 'productos_sueltos'])),

        // Bug #2: era 'fecha_programada' pero la columna es 'vence'. Filtro es_prueba para caja real.
        // Bug #5 (2026-09-02): era solo estado='pendiente', pero los vencidos pasan a 'atrasado'.
        // Mostraba $0 por cobrar habiendo $6.821 atrasados. Ahora cuenta pendiente + atrasado.
        supabase.from('pagos').select('*', { count: 'exact', head: true }).in('estado', ['pendiente', 'atrasado']).eq('es_prueba', false).lte('vence', fechaHoy),
        supabase.from('pagos').select('monto').in('estado', ['pendiente', 'atrasado']).eq('es_prueba', false).lte('vence', fechaHoy),
        safeCount(supabase.from('asientos_contables').select('*', { count: 'exact', head: true }).eq('estado', 'borrador')),

        supabase.from('leads').select('*', { count: 'exact', head: true }).eq('estado', 'nuevo'),
        // Bug #3: estado real es 'nuevo' (masculino singular), no 'nueva'/'en_proceso'.
        safeCount(supabase.from('consultas_comerciales').select('*', { count: 'exact', head: true }).eq('estado', 'nuevo')),
        // Bug #4: estado real es 'pendiente', no 'nueva'. Nunca mostraba las postulaciones abiertas.
        safeCount(supabase.from('porteros_solicitudes').select('*', { count: 'exact', head: true }).eq('estado', 'pendiente')),

        // Fix 9-jul-2026: excluir cuentas de prueba (profiles.es_test) del conteo de suscriptores.
        supabase.from('clientes').select('id, titular:profiles!clientes_titular_fkey!inner(es_test)', { count: 'exact', head: true }).eq('estado', 'activa').eq('titular.es_test', false),
        // Fix 7-jul-2026: la columna es 'estado_pipeline' con valor 'activo' (no 'estado'='activa').
        // safeCount tragaba el error y devolvía 0 → panel mostraba "0 empresas B2B" falso.
        safeCount(supabase.from('empresas').select('*', { count: 'exact', head: true }).eq('estado_pipeline', 'activo').is('archivada_at', null)),

        // Despachos ML: ventas realmente pendientes de despacho (shipping_status pre-envio).
        // Fix 2026-09-02: antes era date_shipped=null, que contaba ventas viejas sin shipping_id
        // (ej. la de juan marzol, sin envio en ML) como "por despachar". Ahora mira el estado real.
        safeQuery(supabase.from('ml_ventas').select('id, shipping_mode, es_flex').neq('status', 'cancelled').in('shipping_status', ['ready_to_ship', 'handling', 'pending', 'ready_to_print'])),

        // Caja del día — pedidos B2C directos (compra única + sueltos) pagados hoy. La suscripción ya va por
        // `pagos` (se excluye tipo 'suscripcion' para no duplicar). ML se cobra aparte, no entra en caja del día.
        safeQuery(supabase.from('pedidos').select('monto_total').in('tipo', ['compra_unica', 'productos_sueltos']).eq('es_prueba', false).not('estado', 'in', '(cancelado,rechazado)').gte('pagado_at', fechaHoy + 'T00:00:00').lte('pagado_at', fechaHoy + 'T23:59:59')),
      ])

      const totalCajaHoy = (cajaHoy || []).reduce((s, p) => s + Number(p.monto || 0), 0)
        + (cajaHoyPedidos || []).reduce((s, p) => s + Number(p.monto_total || 0), 0)
      const totalCobrosPend = (cobrosPendientesMonto || []).reduce((s, p) => s + Number(p.monto || 0), 0)
      const stockCritico = (alertasStock || []).filter(a => a.nivel === 'critico').length
      // Agotados = sin stock vendible (disponible <= 0) → la tienda YA los oculta.
      // Los demas 'critico' tienen 1-2 unidades y siguen a la venta (stock en minimo).
      const stockAgotado = (alertasStock || []).filter(a => a.nivel === 'critico' && (Number(a.stock_actual) - Number(a.stock_reservado || 0)) <= 0).length

      setData({
        // Común
        entregasHoy: entregasHoy || 0,
        entregasHoyEntregadas: entregasHoyEntregadas || 0,
        cajaHoy: totalCajaHoy,
        stockCritico: stockCritico,
        stockAgotado: stockAgotado,
        crmNoLeidos: crmNoLeidos || 0,

        // Operativa
        crmPendientesPlus2h: crmPendientesPlus2h || 0,
        entregasSinAsignar: entregasSinAsignar || 0,
        pedidosSinArmar: pedidosSinArmar || 0,

        // Finanzas
        cobrosPendientesHoy: cobrosPendientesHoy || 0,
        cobrosPendientesMonto: totalCobrosPend,
        asientosBorrador: asientosBorrador || 0,

        // Comercial
        leadsNuevos: leadsNuevos || 0,
        consultasAbiertas: consultasAbiertas || 0,
        postulacionesPorteros: postulacionesPorteros || 0,

        // Informativo
        clientesActivos: clientesActivos || 0,
        empresasActivas: empresasActivas || 0,

        // Despachos ML (común)
        despachosML: despachosML?.length || 0,
        despachosMLFlex: (despachosML || []).filter(v => v.es_flex === true).length,
      })
    }
    load()
    const intervalo = setInterval(load, 60000) // refresh cada 60s
    return () => clearInterval(intervalo)
  }, [user?.id])

  const primerNombre = (profile?.nombre || 'a todos').split(' ')[0]
  const fechaLabel = new Date().toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
  const fmt$ = (n) => `$${Math.round(n || 0).toLocaleString('es-UY')}`

  return (
    <AdminLayout>
      <style>{CSS}</style>
      <div className="dash-wrap">

        {/* Header — resumen del negocio */}
        <header className="dash-head">
          <div>
            <h1>Panel de control</h1>
            <p className="dash-sub">Hola {primerNombre} · {fechaLabel} · {data?.clientesActivos ?? '—'} suscriptores · {data?.empresasActivas ?? 0} empresas B2B</p>
          </div>
        </header>

        {/* HERO FOCO DEL DÍA — dinámico según prioridad (Nico 7-jul-2026) */}
        {data && <HeroFoco data={data} primerNombre={primerNombre} />}

        {/* Canales · resumen consolidado */}
        <CanalesResumenSection />

        {/* Operación del día — común */}
        <section className="dash-section">
          <h2 className="dash-h2">📅 Operación del día</h2>
          <div className="dash-row-common">
            <StatChip icono="🚚" label="Entregas hoy" valor={`${data?.entregasHoyEntregadas ?? 0}/${data?.entregasHoy ?? 0}`} sub="entregadas/total" to="/admin/entregas?fecha=hoy" />
            <StatChip icono="🤝" label="Despachos ML" valor={data?.despachosML ?? 0}
              sub={data?.despachosML > 0 ? (data?.despachosMLFlex > 0 ? `${data.despachosMLFlex} Flex` : 'por despachar') : 'sin backlog'}
              to="/admin/canales/mercadolibre/despacho"
              tono={data?.despachosML > 0 ? 'warning' : 'ok'} />
            <StatChip icono="💰" label="Caja del día" valor={fmt$(data?.cajaHoy)} sub="cobrado hoy" to="/admin/cobros?fecha=hoy" />
            <StatChip icono="📦" label="Stock crítico" valor={data?.stockCritico ?? 0} sub={data?.stockCritico > 0 ? 'productos en alerta' : 'todo OK'} to={data?.stockCritico > 0 ? '/admin/stock?filtro=critico' : '/admin/stock'} tono={data?.stockCritico > 0 ? 'danger' : 'ok'} />
            <StatChip icono="💬" label="CRM" valor={data?.crmNoLeidos ?? 0} sub="conversaciones sin leer" to="/admin/crm?filtro=no_leidas" tono={data?.crmNoLeidos > 0 ? 'warning' : 'ok'} />
          </div>
        </section>

        {/* 3 columnas por área */}
        <div className="dash-cols-3">

          {/* OPERATIVA — Nico */}
          <section className="dash-col">
            <div className="dash-col-head dash-col-operativa">
              <h2>⚡ Operativa</h2>
              <span>Nico</span>
            </div>
            <CardTask
              hay={data?.crmPendientesPlus2h > 0}
              icono="💬"
              titulo={`${data?.crmPendientesPlus2h} mensajes sin responder`}
              detalle="del cliente, hace +2h"
              to="/admin/crm?filtro=pendiente"
              tono="warning"
            />
            <CardTask
              hay={data?.entregasSinAsignar > 0}
              icono="🚐"
              titulo={`${data?.entregasSinAsignar} entregas sin asignar`}
              detalle="programadas para hoy"
              to="/admin/entregas?fecha=hoy&sin_repartidor=1"
              tono="danger"
            />
            <CardTask
              hay={data?.pedidosSinArmar > 0}
              icono="📋"
              titulo={`${data?.pedidosSinArmar} pedidos para armar`}
              detalle="pagados, sin armar"
              to="/admin/pedidos?estado=pagado"
              tono="info"
            />
            <CardTask
              hay={data?.stockCritico > 0}
              icono="📦"
              titulo={`${data?.stockCritico} productos críticos`}
              detalle="desaparecen de la tienda"
              to="/admin/stock?filtro=critico"
              tono="danger"
            />
            <EmptyOk hay={(data?.crmPendientesPlus2h || 0) + (data?.entregasSinAsignar || 0) + (data?.pedidosSinArmar || 0) + (data?.stockCritico || 0) === 0} />
          </section>

          {/* FINANZAS — Roberto */}
          <section className="dash-col">
            <div className="dash-col-head dash-col-finanzas">
              <h2>💳 Finanzas</h2>
              <span>Roberto</span>
            </div>
            {isSuperadmin && (
              <div style={{ marginBottom: 10 }}>
                <VencimientosFiscalesSection mode="card" />
              </div>
            )}
            <CardTask
              hay={data?.cobrosPendientesHoy > 0}
              icono="💸"
              titulo={`${fmt$(data?.cobrosPendientesMonto)} en cobros pendientes`}
              detalle={`${data?.cobrosPendientesHoy} cobros vencidos/hoy`}
              to="/admin/cobros?estado=pendiente"
              tono="warning"
            />
            <CardTask
              hay={data?.asientosBorrador > 0}
              icono="📝"
              titulo={`${data?.asientosBorrador} asientos en borrador`}
              detalle="esperando confirmar"
              to="/admin/finanzas/asientos"
              tono="info"
            />
            <CardTask
              hay={true}
              icono="📊"
              titulo="Cierre del mes"
              detalle="exportar libro diario a Zeta"
              to="/admin/contabilidad"
              tono="info"
            />
            <CardTask
              hay={true}
              icono="📥"
              titulo="Conciliación bancaria"
              detalle="revisar movimientos recientes"
              to="/admin/finanzas/conciliacion"
              tono="info"
            />
            <EmptyOk hay={(data?.cobrosPendientesHoy || 0) + (data?.asientosBorrador || 0) === 0} />
          </section>

          {/* COMERCIAL — Gimena */}
          <section className="dash-col">
            <div className="dash-col-head dash-col-comercial">
              <h2>👥 Comercial</h2>
              <span>Gimena</span>
            </div>
            <CardTask
              hay={data?.leadsNuevos > 0}
              icono="🆕"
              titulo={`${data?.leadsNuevos} leads sin contactar`}
              detalle="formularios sin tocar"
              to="/admin/leads?estado=nuevo"
              tono="warning"
            />
            <CardTask
              hay={data?.consultasAbiertas > 0}
              icono="💬"
              titulo={`${data?.consultasAbiertas} consultas abiertas`}
              detalle="esperando seguimiento"
              to="/admin/consultas-comerciales?estado=nuevo"
              tono="info"
            />
            <CardTask
              hay={data?.postulacionesPorteros > 0}
              icono="🛎️"
              titulo={`${data?.postulacionesPorteros} postulaciones porteros`}
              detalle="sin evaluar"
              to="/admin/postulaciones-porteros?estado=pendiente"
              tono="info"
            />
            <CardTask
              hay={true}
              icono="🏢"
              titulo={`${data?.empresasActivas ?? 0} empresas B2B`}
              detalle="ver pipeline + presupuestos"
              to="/admin/empresas"
              tono="info"
            />
            <EmptyOk hay={(data?.leadsNuevos || 0) + (data?.consultasAbiertas || 0) + (data?.postulacionesPorteros || 0) === 0} />
          </section>

        </div>

        {/* Footer — link a reportes financieros */}
        <div className="dash-footer">
          <span>Datos en vivo · refresca cada 60 seg</span>
          <Link to="/admin/reportes">Ver reportes financieros completos →</Link>
        </div>

      </div>
    </AdminLayout>
  )
}

// ────────────────────────────────────────────────────────────
// Sub-componentes
// ────────────────────────────────────────────────────────────

/**
 * HeroFoco — Card gigante arriba del dashboard con "lo más importante HOY".
 * Elige 1 solo mensaje según prioridad (Nico 7-jul-2026). Fallback celebratorio si todo tranqui.
 */
function HeroFoco({ data, primerNombre }) {
  const fmt$ = (n) => `$${Math.round(n || 0).toLocaleString('es-UY')}`

  // Prioridad de arriba hacia abajo — el primer match gana
  const foco =
    data.entregasSinAsignar > 0 ? {
      tono: 'critico', emoji: '🚚',
      eyebrow: 'HOY sin repartidor',
      titulo: `${data.entregasSinAsignar} ${data.entregasSinAsignar === 1 ? 'entrega' : 'entregas'} sin asignar`,
      sub: 'Programadas para hoy — no salen si no las asignás',
      ctaLabel: 'Asignar ahora',
      ctaTo: '/admin/entregas',
    } :
    data.stockCritico > 0 ? {
      tono: 'critico', emoji: '📦',
      eyebrow: 'Stock crítico',
      titulo: `${data.stockCritico} ${data.stockCritico === 1 ? 'producto crítico' : 'productos críticos'}`,
      sub: data.stockAgotado === 0
        ? 'En mínimo — reponé antes de que se agoten'
        : data.stockAgotado >= data.stockCritico
          ? 'Agotados y ocultos de la tienda hasta reponer'
          : `${data.stockAgotado} agotados (ocultos) · ${data.stockCritico - data.stockAgotado} en mínimo`,
      ctaLabel: 'Ver stock crítico',
      ctaTo: '/admin/stock?filtro=critico',
    } :
    data.cobrosPendientesHoy > 0 ? {
      tono: 'atencion', emoji: '💸',
      eyebrow: 'Cobros vencidos',
      titulo: `${fmt$(data.cobrosPendientesMonto)} por cobrar`,
      sub: `${data.cobrosPendientesHoy} pagos vencidos o de hoy`,
      ctaLabel: 'Ver cobros',
      ctaTo: '/admin/cobros',
    } :
    data.crmPendientesPlus2h > 0 ? {
      tono: 'atencion', emoji: '💬',
      eyebrow: 'Clientes esperando',
      titulo: `${data.crmPendientesPlus2h} ${data.crmPendientesPlus2h === 1 ? 'mensaje' : 'mensajes'} sin responder hace +2h`,
      sub: 'Cuanto más tarde, más frío el lead',
      ctaLabel: 'Responder',
      ctaTo: '/admin/crm?filtro=pendiente',
    } :
    data.despachosML > 0 ? {
      tono: 'atencion', emoji: '🤝',
      eyebrow: 'MercadoLibre',
      titulo: `${data.despachosML} ${data.despachosML === 1 ? 'venta' : 'ventas'} por despachar`,
      sub: data.despachosMLFlex > 0 ? `${data.despachosMLFlex} son Flex (hoy)` : 'Empaquetar y despachar',
      ctaLabel: 'Ir a despachos',
      ctaTo: '/admin/canales/mercadolibre/despacho',
    } :
    data.pedidosSinArmar > 0 ? {
      tono: 'medio', emoji: '📋',
      eyebrow: 'Pedidos pagados',
      titulo: `${data.pedidosSinArmar} ${data.pedidosSinArmar === 1 ? 'pedido' : 'pedidos'} para armar`,
      sub: 'Ya pagados, esperando armar y despachar',
      ctaLabel: 'Ver pedidos',
      ctaTo: '/admin/pedidos?estado=pagado',
    } :
    data.postulacionesPorteros > 0 ? {
      tono: 'oportunidad', emoji: '🛎️',
      eyebrow: 'Nuevas postulaciones',
      titulo: `${data.postulacionesPorteros} ${data.postulacionesPorteros === 1 ? 'portero postulado' : 'porteros postulados'}`,
      sub: 'Aprovechá para sumar red antes de que se enfríen',
      ctaLabel: 'Evaluar',
      ctaTo: '/admin/postulaciones-porteros',
    } :
    data.consultasAbiertas > 0 ? {
      tono: 'oportunidad', emoji: '🎯',
      eyebrow: 'Leads B2B',
      titulo: `${data.consultasAbiertas} ${data.consultasAbiertas === 1 ? 'consulta' : 'consultas'} nuevas`,
      sub: 'Empresas pidiendo cotización — respondé rápido',
      ctaLabel: 'Ver consultas',
      ctaTo: '/admin/consultas-comerciales',
    } :
    data.leadsNuevos > 0 ? {
      tono: 'oportunidad', emoji: '🆕',
      eyebrow: 'Leads',
      titulo: `${data.leadsNuevos} ${data.leadsNuevos === 1 ? 'lead nuevo' : 'leads nuevos'}`,
      sub: 'Formularios sin contactar',
      ctaLabel: 'Ver leads',
      ctaTo: '/admin/leads',
    } :
    {
      // Fallback celebratorio — nada urgente pendiente
      tono: 'zen', emoji: '☀️',
      eyebrow: 'Todo tranquilo',
      titulo: `Buen día, ${primerNombre}`,
      sub: `${data.clientesActivos || 0} suscriptores activos · ${data.empresasActivas || 0} empresas B2B · caja del día ${fmt$(data.cajaHoy)}`,
      ctaLabel: 'Ver métricas',
      ctaTo: '/admin/reportes',
    }

  return (
    <Link to={foco.ctaTo} className={`dash-hero dash-hero-${foco.tono}`}>
      <div className="dash-hero-icono">{foco.emoji}</div>
      <div className="dash-hero-body">
        <div className="dash-hero-eyebrow">{foco.eyebrow}</div>
        <div className="dash-hero-titulo">{foco.titulo}</div>
        <div className="dash-hero-sub">{foco.sub}</div>
      </div>
      <div className="dash-hero-cta">
        <span>{foco.ctaLabel}</span>
        <span className="dash-hero-arrow">→</span>
      </div>
    </Link>
  )
}

function StatChip({ icono, label, valor, sub, to, tono = 'neutral' }) {
  return (
    <Link to={to} className={`dash-chip dash-chip-${tono}`}>
      <div className="dash-chip-icono">{icono}</div>
      <div className="dash-chip-body">
        <div className="dash-chip-label">{label}</div>
        <div className="dash-chip-valor">{valor}</div>
        <div className="dash-chip-sub">{sub}</div>
      </div>
    </Link>
  )
}

function CardTask({ hay, icono, titulo, detalle, to, tono = 'info' }) {
  if (!hay) return null
  return (
    <Link to={to} className={`dash-task dash-task-${tono}`}>
      <div className="dash-task-icono">{icono}</div>
      <div className="dash-task-body">
        <div className="dash-task-titulo">{titulo}</div>
        <div className="dash-task-detalle">{detalle}</div>
      </div>
      <div className="dash-task-arrow">→</div>
    </Link>
  )
}

function EmptyOk({ hay }) {
  if (!hay) return null
  return (
    <div className="dash-empty">
      <div className="dash-empty-icono">🎉</div>
      <div className="dash-empty-msg">Todo al día acá. Aprovechá para tomarte un café.</div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : '' }

// Algunas tablas/columnas pueden no existir todavía o tirar 42P01 / 42703.
// Esto evita que el dashboard entero se rompa por una sola query.
async function safeCount(query) {
  try {
    const r = await query
    if (r?.error) return { count: 0, error: r.error }
    return r
  } catch (e) {
    return { count: 0, error: e }
  }
}

// Wrapper para queries que devuelven { data, error } (usamos data como array).
async function safeQuery(query) {
  try {
    const r = await query
    if (r?.error) return { data: [], error: r.error }
    return { data: r.data || [], error: null }
  } catch (e) {
    return { data: [], error: e }
  }
}

// Wrapper para promesas que devuelven array (como adminGetAlertasStock).
async function safeAlertas(promesa) {
  try {
    const r = await promesa
    return Array.isArray(r) ? r : []
  } catch (e) {
    return []
  }
}

// ────────────────────────────────────────────────────────────
// CSS
// ────────────────────────────────────────────────────────────

const CSS = `
.dash-wrap{
  --navy:#1A3461; --teal:#00A8B5; --sky:#5BBDD8;
  --surface:#fff; --surface-2:#F7F9FC; --paper:#F1F5F9;
  --ink:#0F1F3D; --ink-2:#3B4A66; --ink-3:#66748F; --ink-4:#98A3B8;
  --hair:rgba(26,52,97,.10); --hair-2:rgba(26,52,97,.06);
  --teal-soft:rgba(0,168,181,.10); --sky-soft:rgba(91,189,216,.16);
  --ok:#1A8F56; --warn:#B45309; --bad:#B0413E;
  --display:Manrope, Inter, system-ui, sans-serif;
  --sh:0 1px 1px rgba(26,52,97,.03), 0 2px 6px -1px rgba(26,52,97,.05), 0 16px 36px -16px rgba(26,52,97,.12);
  --sh-h:0 1px 1px rgba(26,52,97,.03), 0 6px 14px -4px rgba(26,52,97,.10), 0 26px 46px -18px rgba(26,52,97,.18);
  max-width:1160px; margin:0 auto; padding:0 0 8px;
  font-family:Inter, -apple-system, system-ui, sans-serif; color:var(--ink);
}
/* fill responsive en monitores grandes (tandem con zoom de AdminLayout) */
@media (min-width:1500px){ .dash-wrap{ max-width:1320px } }
@media (min-width:1800px){ .dash-wrap{ max-width:1480px } }
@media (min-width:2200px){ .dash-wrap{ max-width:1680px } }

.dash-head{ margin-bottom:20px }
.dash-head h1{ font-family:var(--display); font-size:28px; font-weight:800; letter-spacing:-.03em; margin:0 0 4px; color:var(--ink) }
.dash-sub{ font-size:13px; color:var(--ink-3); margin:0 }

/* HERO FOCO DEL DÍA */
.dash-hero{ display:flex; align-items:center; gap:18px; padding:22px 26px; border-radius:18px; text-decoration:none; margin-bottom:22px; transition:transform .2s, box-shadow .2s; box-shadow:var(--sh) }
.dash-hero:hover{ transform:translateY(-2px); box-shadow:var(--sh-h) }
.dash-hero-icono{ font-size:42px; line-height:1; flex-shrink:0; filter:drop-shadow(0 3px 8px rgba(0,0,0,.18)) }
.dash-hero-body{ flex:1; min-width:0 }
.dash-hero-eyebrow{ font-size:11px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; opacity:.72; margin-bottom:4px }
.dash-hero-titulo{ font-family:var(--display); font-size:22px; font-weight:800; line-height:1.15; letter-spacing:-.02em; margin-bottom:4px }
.dash-hero-sub{ font-size:13px; opacity:.85; line-height:1.35 }
.dash-hero-cta{ display:flex; align-items:center; gap:6px; padding:10px 18px; border-radius:999px; background:rgba(255,255,255,.18); -webkit-backdrop-filter:blur(4px); backdrop-filter:blur(4px); font-size:13px; font-weight:700; flex-shrink:0 }
.dash-hero-arrow{ font-size:18px; font-weight:300 }
.dash-hero-critico{ background:linear-gradient(135deg,#DC2626,#B91C1C); color:#fff }
.dash-hero-atencion{ background:linear-gradient(135deg,#D97706,#B45309); color:#fff }
.dash-hero-medio{ background:linear-gradient(135deg,#1E3B6E,#12264a); color:#fff }
.dash-hero-oportunidad{ background:linear-gradient(135deg,#00A8B5,#0A7C86); color:#fff }
.dash-hero-zen{ background:linear-gradient(135deg,#fff,var(--surface-2)); color:var(--navy); box-shadow:inset 0 0 0 1px var(--hair-2), var(--sh) }
.dash-hero-zen .dash-hero-eyebrow{ color:var(--teal); opacity:1 }
.dash-hero-zen .dash-hero-cta{ background:linear-gradient(135deg,var(--teal),var(--sky)); color:#fff }
@media (max-width:640px){
  .dash-hero{ padding:18px 20px; gap:14px; flex-wrap:wrap }
  .dash-hero-icono{ font-size:34px }
  .dash-hero-titulo{ font-size:18px }
  .dash-hero-cta{ width:100%; justify-content:center; margin-top:8px }
}

/* Sections */
.dash-section{ margin-bottom:22px }
.dash-h2{ font-size:12px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--ink-4); margin:0 0 12px }

/* Fila de stats */
.dash-row-common{ display:grid; grid-template-columns:repeat(auto-fit,minmax(184px,1fr)); gap:12px }
.dash-chip{ display:flex; gap:12px; align-items:center; background:var(--surface); border-radius:14px; padding:14px 16px; text-decoration:none; color:var(--ink); box-shadow:inset 0 0 0 1px var(--hair-2), var(--sh); transition:transform .15s, box-shadow .15s }
.dash-chip:hover{ transform:translateY(-1px); box-shadow:inset 0 0 0 1px rgba(0,168,181,.35), var(--sh-h) }
.dash-chip-icono{ font-size:20px; width:40px; height:40px; border-radius:11px; background:linear-gradient(135deg,var(--teal-soft),var(--sky-soft)); display:grid; place-items:center; flex:none }
.dash-chip-body{ flex:1; min-width:0 }
.dash-chip-label{ font-size:11px; font-weight:600; color:var(--ink-4); letter-spacing:.05em; text-transform:uppercase }
.dash-chip-valor{ font-family:var(--display); font-size:22px; font-weight:800; letter-spacing:-.02em; margin-top:2px; font-variant-numeric:tabular-nums }
.dash-chip-sub{ font-size:11.5px; color:var(--ink-3); margin-top:1px }
.dash-chip-danger{ border-left:3px solid var(--bad) }
.dash-chip-warning{ border-left:3px solid var(--warn) }
.dash-chip-ok{ border-left:3px solid var(--ok) }
.dash-chip-info{ border-left:3px solid var(--teal) }

/* 3 columnas */
.dash-cols-3{ display:grid; grid-template-columns:1fr 1fr 1fr; gap:14px; margin-bottom:22px }
@media (max-width:980px){ .dash-cols-3{ grid-template-columns:1fr } }
.dash-col{ background:var(--surface-2); border-radius:16px; padding:16px; display:flex; flex-direction:column; gap:8px; box-shadow:inset 0 0 0 1px var(--hair-2) }
.dash-col-head{ display:flex; justify-content:space-between; align-items:baseline; padding-bottom:10px; border-bottom:1px solid var(--hair-2); margin-bottom:4px }
.dash-col-head h2{ font-family:var(--display); font-size:14px; font-weight:800; letter-spacing:-.01em; margin:0 }
.dash-col-head span{ font-size:11px; color:var(--ink-4); font-weight:600; text-transform:uppercase; letter-spacing:.04em }
.dash-col-operativa h2{ color:var(--navy) }
.dash-col-finanzas h2{ color:#0A7C86 }
.dash-col-comercial h2{ color:#B45309 }

.dash-task{ display:flex; gap:10px; align-items:center; background:var(--surface); border-radius:12px; padding:11px 13px; text-decoration:none; color:var(--ink); box-shadow:inset 0 0 0 1px var(--hair-2); transition:transform .15s, box-shadow .15s }
.dash-task:hover{ transform:translateX(2px); box-shadow:inset 0 0 0 1px rgba(0,168,181,.35), var(--sh) }
.dash-task-icono{ font-size:17px; line-height:1; flex-shrink:0 }
.dash-task-body{ flex:1; min-width:0 }
.dash-task-titulo{ font-size:13px; font-weight:700; letter-spacing:-.01em }
.dash-task-detalle{ font-size:11.5px; color:var(--ink-3); margin-top:1px }
.dash-task-arrow{ font-size:16px; color:var(--ink-4); flex-shrink:0; transition:transform .15s, color .15s }
.dash-task:hover .dash-task-arrow{ color:var(--teal); transform:translateX(2px) }
.dash-task-warning{ border-left:3px solid var(--warn) }
.dash-task-danger{ border-left:3px solid var(--bad) }
.dash-task-info{ border-left:3px solid var(--teal) }

.dash-empty{ display:flex; align-items:center; gap:10px; padding:14px 12px; background:rgba(31,157,94,.08); border:1px dashed rgba(31,157,94,.4); border-radius:12px; color:#14532D }
.dash-empty-icono{ font-size:20px }
.dash-empty-msg{ font-size:12px; font-weight:600; line-height:1.4 }

.dash-footer{ margin-top:20px; padding-top:14px; border-top:1px solid var(--hair-2); display:flex; justify-content:space-between; align-items:center; gap:12px; font-size:11.5px; color:var(--ink-4) }
.dash-footer a{ color:var(--ink-3); text-decoration:none; font-weight:600 }
.dash-footer a:hover{ color:var(--teal) }
`
